<template>
  <div class="page-wrapper">
    <div v-if="$apollo.loading">
      <spinner />
    </div>
    <div v-else>
      <v-layout align-center justify-space-between class="mb-8">
        <v-flex class="screen-title mb-0">
          <span>{{ adminText('workflowsAll.sponsors_82cebd0', 'Sponsors') }}</span>
        </v-flex>
        <v-flex shrink>
          <main-button depressed @click="openAdd">
            {{ adminText('workflowsAll.add_sponsor_581e2ac', 'Add sponsor') }}
            <v-icon class="ml-2">add</v-icon>
          </main-button>
        </v-flex>
      </v-layout>

      <div class="mb-32 title-description">
        {{ adminText('workflowsAll.add_the_sponsors_of_your_event_they_appear_in_a40a1e3', 'Add the sponsors of your event. They appear in the attendee app in the order you add them, with their logo on a white tile.') }}
      </div>

      <v-layout v-if="!sponsors.length" class="empty">
        <span>{{ adminText('workflowsAll.no_sponsors_yet_1860085', 'No sponsors yet.') }}</span>
      </v-layout>

      <div v-for="sponsor in sponsors" :key="sponsor.id" class="sponsor-row">
        <div class="logo-plate">
          <img v-if="sponsor.logo" :src="sponsor.logo" class="logo" :alt="workflowLabel(sponsor.name)">
          <v-icon v-else color="#979797">photo</v-icon>
        </div>
        <span class="sponsor-name">{{ workflowLabel(sponsor.name) }}</span>
        <v-btn icon @click="openUpdate(sponsor)">
          <v-icon color="primary">edit</v-icon>
        </v-btn>
      </div>

      <sponsor-dialog
        :show.sync="dialog"
        :action="action"
        :sponsor="selectedSponsor"
        :event-id="eventId"
        @saved="onChanged('workflowsAll.sponsor_saved_f889f5e')"
        @deleted="onChanged('workflowsAll.sponsor_deleted_71f56c0')"
      />

      <simple-snackbar :open.sync="snackbar" color="primary" :message-prop="workflowLabel(snackbarMessage)" />
    </div>
  </div>
</template>

<script>
import WorkflowDisplay from '@/admin-i18n/workflows-all-display'
import SimpleSnackbar from '../shared/snackbars/simple-snackbar'
import Spinner from '../shared/spinner'
import SponsorDialog from './sponsor-dialog.vue'
import { GET_EVENT_SPONSORS } from '../../graphql/sponsors.graphql'

/**
 * COR-1684: the sponsors of a conference event, in the order the attendee app lists them.
 *
 * Order comes from the API (rank groups when an event uses them, creation order otherwise),
 * so the admin and the app cannot disagree about it. Every edit commits from its dialog -
 * there is no page-level save.
 */
export default {
  mixins: [WorkflowDisplay],
  name: 'sponsor-list-container',
  components: {
    SimpleSnackbar,
    Spinner,
    SponsorDialog,
  },
  props: {
    eventId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      sponsors: [],
      dialog: false,
      action: 'add',
      selectedSponsor: null,
      snackbar: false,
      snackbarMessage: '',
    }
  },
  methods: {
    openAdd() {
      this.selectedSponsor = null
      this.action = 'add'
      this.dialog = true
    },
    openUpdate(sponsor) {
      this.selectedSponsor = sponsor
      this.action = 'update'
      this.dialog = true
    },
    async onChanged(message) {
      this.snackbarMessage = message
      this.snackbar = true
      await this.$apollo.queries.getEventSponsors.refetch()
    },
  },
  apollo: {
    getEventSponsors: {
      query: GET_EVENT_SPONSORS,
      variables() {
        return {
          eventId: this.eventId,
        }
      },
      manual: true,
      result({ loading, data }) {
        if (!loading) {
          this.sponsors = data.getEventSponsors
        }
      },
    },
  },
}
</script>

<style scoped>
.sponsor-row {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 16px;
  margin-bottom: 16px;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  background-color: #fafafa;
  max-width: 680px;
}

.logo-plate {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  height: 56px;
  width: 56px;
  padding: 4px;
  border-radius: 8px;
  background-color: #ffffff;
  border: 1px solid #e0e0e0;
}

.logo {
  max-height: 100%;
  max-width: 100%;
  object-fit: contain;
}

.sponsor-name {
  flex: 1;
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 1.25rem;
  color: #333333;
}

.empty {
  color: #979797;
  padding-bottom: 16px;
}

</style>
